import { Avatar } from "./components/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "./components/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "./components/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "./components/sidebar";
import { SidebarLayout } from "./components/sidebar-layout";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
} from "@heroicons/react/16/solid";
import {
  UsersIcon,
  MegaphoneIcon,
  UserGroupIcon,
  TableCellsIcon,
  ScissorsIcon,
  Squares2X2Icon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChartBarIcon,
} from "@heroicons/react/20/solid";
import { Route, Routes, useLocation, useNavigate, Link } from "react-router-dom";
import Horses from "./pages/horses";
import Form from "./pages/form";
import Customers from "./pages/customers";
import Services from "./pages/services";
import Accounts from "./pages/accounts";
import Billing from "./pages/billing";
import Payroll from "./pages/payroll";
import PayrollSummary from "./pages/payroll-summary";
import { useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, db } from "./firebase";
import { Button } from "./components/button";
import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { ReactComponent as HorseIcon } from "./icons/horse-solid.svg";
import Grooms from "./pages/grooms";
import Shows from "./pages/shows";
import LogoWhite from './images/LogoWhite.png'
import LogoBlack from './images/LogoBlack.png'

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const signIn = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then((result) => {
        setCurrentUser(result.user);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const signOutUser = async () => {
    await signOut(auth);
  };

  useEffect(() => {
    const checkAdminStatus = async (user: User) => {
      if (!user || !user.email) return;
      try {
        const adminDocRef = doc(db, "admins", user.email);
        const adminDoc = await getDoc(adminDocRef);
        setIsAdmin(adminDoc.exists());
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    };

    onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        checkAdminStatus(user);
      } else {
        navigate("/");
      }
    });
  }, [navigate]);

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          {currentUser && (
            <NavbarSection>
              <Dropdown>
                <DropdownButton as={NavbarItem}>
                  <Avatar initials={currentUser.email?.charAt(0)} square />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  <DropdownItem onClick={signOutUser}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavbarSection>
          )}
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader>
            <div className="py-5">
              <img
                className="w-32 hidden dark:block"
                src={LogoWhite}
                alt=""
              />
              <img
                className="w-32 block dark:hidden"
                src={LogoBlack}
                alt=""
              />
            </div>
            <SidebarSection className="max-lg:hidden">
              <Link to="/">
                <SidebarItem current={location.pathname === "/"}>
                  <TableCellsIcon />
                  <SidebarLabel>Input Form</SidebarLabel>
                </SidebarItem>
              </Link>
            </SidebarSection>
          </SidebarHeader>

          <SidebarBody>
            {currentUser && isAdmin ? (
              <>
                <SidebarSection>
                  <Link to="/horses">
                    <SidebarItem current={location.pathname === "/horses"}>
                      <HorseIcon fill="currentColor" className="w-5 h-5" />
                      <SidebarLabel>Horses</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/customers">
                    <SidebarItem current={location.pathname === "/customers"}>
                      <UsersIcon />
                      <SidebarLabel>Customers</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/services">
                    <SidebarItem current={location.pathname === "/services"}>
                      <Squares2X2Icon />
                      <SidebarLabel>Services</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/grooms">
                    <SidebarItem current={location.pathname === "/grooms"}>
                      <ScissorsIcon />
                      <SidebarLabel>Grooms</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/shows">
                    <SidebarItem current={location.pathname === "/shows"}>
                      <MegaphoneIcon />
                      <SidebarLabel>Horse Shows</SidebarLabel>
                    </SidebarItem>
                  </Link>
                </SidebarSection>
                <SidebarSection>
                  <Link to="/billing">
                    <SidebarItem current={location.pathname === "/billing"}>
                      <CurrencyDollarIcon fill="currentColor" className="w-5 h-5" />
                      <SidebarLabel>Billing</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/payroll">
                    <SidebarItem current={location.pathname === "/payroll"}>
                      <BanknotesIcon fill="currentColor" className="w-5 h-5" />
                      <SidebarLabel>Payroll</SidebarLabel>
                    </SidebarItem>
                  </Link>
                  <Link to="/payroll-summary">
                    <SidebarItem current={location.pathname === "/payroll-summary"}>
                      <ChartBarIcon fill="currentColor" className="w-5 h-5" />
                      <SidebarLabel>Payroll Summary</SidebarLabel>
                    </SidebarItem>
                  </Link>
                </SidebarSection>
                <SidebarSpacer />
                <SidebarSection>
                  <Link to="/accounts">
                    <SidebarItem current={location.pathname === "/accounts"}>
                      <UserGroupIcon />
                      <SidebarLabel>Accounts</SidebarLabel>
                    </SidebarItem>
                  </Link>
                </SidebarSection>
              </>
            ) : (
              <></>
            )}
          </SidebarBody>

          <SidebarFooter className="max-lg:hidden">
            {currentUser ? (
              <Dropdown>
                <DropdownButton as={SidebarItem}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar
                      initials={currentUser.email?.charAt(0)}
                      className="size-10"
                      square
                      alt=""
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                        {currentUser.displayName}
                      </span>
                      <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                        {currentUser.email}
                      </span>
                    </span>
                  </span>
                  <ChevronUpIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <DropdownItem onClick={signOutUser}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            ) : (
              <Button className="cursor-pointer" onClick={signIn}>
                Sign in
              </Button>
            )}
          </SidebarFooter>
        </Sidebar>
      }
    >
      <Routes>
        <Route path="/" element={<Form />} />
        { isAdmin && <Route path="/horses" element={<Horses />} /> }
        { isAdmin && <Route path="/customers" element={<Customers />} /> }
        { isAdmin && <Route path="/services" element={<Services />} /> }
        { isAdmin && <Route path="/accounts" element={<Accounts />} /> }
        { isAdmin && <Route path="/grooms" element={<Grooms />} /> }
        { isAdmin && <Route path="/shows" element={<Shows />} /> }
        { isAdmin && <Route path="/billing" element={<Billing />} /> }
        { isAdmin && <Route path="/payroll" element={<Payroll />} /> }
        { isAdmin && <Route path="/payroll-summary" element={<PayrollSummary />} /> }
      </Routes>
    </SidebarLayout>
  );
}
